import {useState, Fragment} from "react";
import {Dialog} from "@mui/material";
import {FormattedMessage, defineMessage} from "react-intl";
import {FormActionButtons, DialogActions, DialogTitle, IconDialogContent, msg_cancel} from "@dvrt/graphql-widgets";
import {useDispatch} from "react-redux";
import {showNotification} from "@/actions";
import {useDeleteDomainSubscription} from "./mutations.generated";
import {msg_delete} from "@/utils/translations";

type ContainerProps = {
    close: () => void;
    domainId: number;
    name: string;
    onSuccess: () => void;
};

const DialogContainer = ({close, domainId, name, onSuccess}: ContainerProps) => {
    const [deleting, setDeleting] = useState(false);
    const {data: deleteDomainData} = useDeleteDomainSubscription({
        skip: !deleting,
        variables: {id: domainId},
        onComplete: () => {
            setDeleting(false);
            close();
        },
        onData: ({data}) => {
            if (data.data?.deleteDomain.errors) {
                dispatch(
                    showNotification(
                        defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                        "error",
                    ),
                );
                close();
            } else if (data.data?.deleteDomain.progress === 1) {
                dispatch(
                    showNotification(
                        defineMessage({
                            id: "domainDeleted",
                            defaultMessage: "Domain has been successfully deleted.",
                        }),
                        "success",
                    ),
                );
                onSuccess();
            }
        },
    });
    const dispatch = useDispatch();

    function cancel() {
        close();
    }

    return (
        <Fragment>
            <DialogTitle>
                <FormattedMessage id="deleteDomain" defaultMessage="Delete domain" values={{name}} />
            </DialogTitle>
            <IconDialogContent icon="question-circle">
                <FormattedMessage
                    id="confirms.delete_domain"
                    defaultMessage='Do you really want to delete "{name}"?'
                    values={{name}}
                />
            </IconDialogContent>
            <DialogActions>
                <FormActionButtons
                    mainButtonText={msg_delete}
                    secondaryButtonText={msg_cancel}
                    mainAction={() => setDeleting(true)}
                    secondaryAction={cancel}
                    variant="text"
                    mainButtonColor="secondary"
                    enabled={!deleting}
                    pending={deleting}
                    progress={deleteDomainData?.deleteDomain.progress * 100}
                />
            </DialogActions>
        </Fragment>
    );
};

type Props = {
    isOpen: boolean;
} & ContainerProps;

export default ({isOpen, close, domainId, name, onSuccess}: Props) => {
    const onDialogClose = (_: object, reason: string) => {
        if (reason === "backdropClick") return;
        close();
    };

    return (
        <Dialog open={isOpen} onClose={onDialogClose} maxWidth="lg">
            <DialogContainer close={close} domainId={domainId} name={name} onSuccess={onSuccess} />
        </Dialog>
    );
};
